import React, { useEffect, useState } from 'react';
import { createOrg, updateOrg, getOrgs } from '../../API/adminAPI';

const ORG_TYPES = [
  { value: 'atoms',       label: 'ATOMS (Super Admin org)' },
  { value: 'oem',         label: 'OEM / Manufacturer' },
  { value: 'distributor', label: 'Distributor / Reseller' },
];

const toSlug = name => name.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

const Label = ({ children, required }) => (
  <label style={{ display: 'block', fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500, color: 'var(--ink-700)', marginBottom: 5 }}>
    {children}{required && <span style={{ color: 'var(--offline)', marginLeft: 3 }}>*</span>}
  </label>
);
const Hint = ({ children }) => (
  <div style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--ink-500)', marginTop: 4 }}>{children}</div>
);
const Field = ({ children }) => <div style={{ marginBottom: 16 }}>{children}</div>;

const inputStyle = {
  width: '100%', padding: '9px 12px',
  fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--ink-900)',
  background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--r-md)', outline: 'none', boxSizing: 'border-box',
  transition: 'border-color var(--t-fast)',
};

const CloseIcon = 'M18 6L6 18 M6 6l12 12';
const Icon = ({ d, size = 15, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const OrgWizard = ({ org, onClose }) => {
  const isEdit = Boolean(org);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const [oems,   setOems]   = useState([]);

  const [name,        setName]        = useState(org?.name          || '');
  const [orgType,     setOrgType]     = useState(org?.org_type      || 'oem');
  const [parentOemId, setParentOemId] = useState(org?.parent_oem_id ? String(org.parent_oem_id) : '');
  const [logoUrl,     setLogoUrl]     = useState(org?.logo_url      || '');
  const [color,       setColor]       = useState(org?.primary_color || '#0897BE');
  const [isActive,    setIsActive]    = useState(org?.is_active     ?? true);
  const [nameErr,     setNameErr]     = useState('');

  useEffect(() => {
    getOrgs().then(d => {
      setOems((d.organisations || []).filter(o => o.org_type === 'oem').map(o => ({ value: String(o.id), label: o.name })));
    }).catch(() => {});
  }, []);

  const save = async () => {
    if (!name.trim()) { setNameErr('Name is required'); return; }
    setNameErr('');
    setSaving(true); setError('');
    try {
      const payload = {
        name: name.trim(), slug: toSlug(name),
        org_type: orgType,
        parent_oem_id: parentOemId ? parseInt(parentOemId) : null,
        logo_url: logoUrl.trim() || '',
        primary_color: color,
        is_active: isActive,
      };
      if (isEdit) await updateOrg(org.id, payload);
      else await createOrg(payload);
      onClose(true);
    } catch (e) {
      setError(e?.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, display: 'flex' }}>
      <div style={{ flex: 1, background: 'rgba(12,14,22,.32)', backdropFilter: 'blur(3px)' }} onClick={() => onClose(false)} />
      <div style={{ width: 460, background: 'var(--bg-surface)', borderLeft: '1px solid var(--border-hair)', display: 'flex', flexDirection: 'column', boxShadow: 'var(--sh-lg)' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border-hair)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 15, color: 'var(--ink-900)' }}>
            {isEdit ? `Edit — ${org.name}` : 'New Organisation'}
          </div>
          <button onClick={() => onClose(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-500)', padding: 4 }}>
            <Icon d={CloseIcon} size={18} />
          </button>
        </div>

        {/* Form */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          {error && <div style={{ background: 'var(--offline-bg)', color: 'var(--offline)', borderRadius: 'var(--r-md)', padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>{error}</div>}

          <Field>
            <Label required>Organisation Name</Label>
            <input value={name} onChange={e => { setName(e.target.value); setNameErr(''); }}
              placeholder="e.g. Maithri Aquatech" style={{ ...inputStyle, borderColor: nameErr ? 'var(--offline)' : undefined }} />
            {nameErr && <div style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--offline)', marginTop: 4 }}>{nameErr}</div>}
          </Field>

          <Field>
            <Label required>Type</Label>
            <select value={orgType} onChange={e => setOrgType(e.target.value)} disabled={isEdit}
              style={{ ...inputStyle, cursor: isEdit ? 'not-allowed' : 'pointer', opacity: isEdit ? 0.6 : 1 }}>
              {ORG_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            {isEdit && <Hint>Type cannot be changed after creation</Hint>}
          </Field>

          {orgType === 'distributor' && (
            <Field>
              <Label>Parent OEM</Label>
              <select value={parentOemId} onChange={e => setParentOemId(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="">Select OEM this distributor belongs to</option>
                {oems.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
          )}

          <Field>
            <Label>Logo URL</Label>
            <input value={logoUrl} onChange={e => setLogoUrl(e.target.value)}
              placeholder="https://example.com/logo.png" style={inputStyle} />
            <Hint>Shown in top bar for all users in this org</Hint>
            {logoUrl && (
              <div style={{ marginTop: 10, padding: 10, border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', textAlign: 'center', background: 'var(--bg-raised)' }}>
                <div style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--ink-500)', marginBottom: 6 }}>Preview</div>
                <img src={logoUrl} alt="logo" style={{ maxHeight: 40, objectFit: 'contain' }} />
              </div>
            )}
          </Field>

          <Field>
            <Label>Brand Colour</Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ position: 'relative', width: 40, height: 38, flexShrink: 0 }}>
                <div style={{ width: 38, height: 38, borderRadius: 'var(--r-md)', background: color, border: '1px solid var(--border-subtle)', cursor: 'pointer' }} />
                <input type="color" value={color} onChange={e => setColor(e.target.value)}
                  style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }} />
              </div>
              <input value={color} onChange={e => setColor(e.target.value)} maxLength={7}
                style={{ ...inputStyle, fontFamily: 'var(--font-mono)', flex: 1 }} />
            </div>
            <Hint>Primary accent colour for this org's UI</Hint>
          </Field>

          <Field>
            <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
              <div
                onClick={() => setIsActive(v => !v)}
                style={{ width: 36, height: 20, borderRadius: 10, background: isActive ? 'var(--online)' : 'var(--border-strong)', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
                <div style={{ position: 'absolute', top: 2, left: isActive ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
              </div>
              <div>
                <div style={{ fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500, color: 'var(--ink-700)' }}>Active</div>
                <div style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--ink-500)' }}>Inactive orgs — all their users are locked out immediately</div>
              </div>
            </label>
          </Field>
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border-hair)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={() => onClose(false)} style={{ background: 'none', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', padding: '8px 16px', fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--ink-500)', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={save} disabled={saving}
            style={{ background: saving ? 'var(--border-strong)' : 'var(--accent)', border: 'none', borderRadius: 'var(--r-md)', padding: '8px 20px', fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500, color: '#fff', cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Organisation'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrgWizard;
